/**
 * MarketAnalyticsPage — Admin MLS market intelligence for sales prospecting.
 *
 * KOPS-513: reads from kairos_market_analytics.mls_offices + mls_members.
 * Shows the ENTIRE ARMLS market (every brokerage, every agent) — not just
 * KairosIQ customers. This is the sales team's prospecting dashboard.
 *
 * Route: /admin/market-analytics
 *
 * Backend:
 *   GET /api/broker/admin/market-analytics                     → office list + stats
 *   GET /api/broker/admin/market-analytics/office/:id/members  → member roster
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding, faSearch, faUsers, faMapMarkerAlt,
  faPhone, faEnvelope, faIdCard, faCrown, faGlobe,
  faChartLine, faFilter, faDatabase, faFilePdf,
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';

const colors = {
  navy: '#0F1E35', navy2: '#162844', navy3: '#1E3557',
  gold: '#C9952A', gold2: '#E8B44A',
  white: '#FFFFFF', off: '#E8EEF6', offBg: '#eef1f6',
  muted: '#8FA3BF', green: '#27ae60', red: '#e74c3c',
  orange: '#f39c12', blue: '#3498db', border: '#dee2e6',
};
const fonts = { display: "'Bebas Neue', Impact, sans-serif", body: "'DM Sans', -apple-system, sans-serif" };
const FONT_LINK = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';

interface MLSOffice {
  id: string;
  spark_id: string;
  short_id: string | null;
  office_name: string | null;
  mls_name: string | null;
  board: string | null;
  license_number: string | null;
  broker_short_id: string | null;
  agent_count: number;
  is_active: number;
  phone: string | null;
  email: string | null;
  city: string | null;
  state: string | null;
  full_address: string | null;
}

interface MLSMember {
  id: string;
  short_id: string | null;
  member_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  license_number: string | null;
  local_type: string | null;
  office_name: string | null;
  photo_url: string | null;
  is_active: number;
  is_kairosiq_customer: number;
}

const MarketAnalyticsPage: React.FC = () => {
  const [offices, setOffices] = useState<MLSOffice[]>([]);
  const [stats, setStats] = useState({ total_offices: 0, total_agents: 0, cities: 0, boards: 0, avg_office_size: 0, largest_office: 0 });
  const [insights, setInsights] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<MLSOffice | null>(null);
  const [members, setMembers] = useState<MLSMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [performance, setPerformance] = useState<any>(null);
  const [perfLoading, setPerfLoading] = useState(false);
  const [detailTab, setDetailTab] = useState<'roster' | 'performance' | 'retention'>('roster');
  const [retentionData, setRetentionData] = useState<any>(null);
  const [retentionLoading, setRetentionLoading] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const [agentLostDeals, setAgentLostDeals] = useState<any[]>([]);
  const [invitingAgent, setInvitingAgent] = useState<string | null>(null);
  const [invitedAgents, setInvitedAgents] = useState<Set<string>>(new Set());
  const [agentDealsLoading, setAgentDealsLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [searchMode, setSearchMode] = useState<'offices' | 'agents'>('offices');
  const [agentSearchResults, setAgentSearchResults] = useState<any[]>([]);
  const [agentSearchLoading, setAgentSearchLoading] = useState(false);
  const [agentDetail, setAgentDetail] = useState<any>(null);
  const [agentProduction, setAgentProduction] = useState<any>(null);
  const [agentProdLoading, setAgentProdLoading] = useState(false);
  const [agentReportLoading, setAgentReportLoading] = useState(false);
  const [agentDetailTab, setAgentDetailTab] = useState<'production' | 'retention'>('production');
  const [agentRetention, setAgentRetention] = useState<any>(null);
  const [agentRetentionLoading, setAgentRetentionLoading] = useState(false);
  const [agentLostDealsDetail, setAgentLostDealsDetail] = useState<any[]>([]);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [minAgents, setMinAgents] = useState('');

  const fetchOffices = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (search) params.search = search;
      if (cityFilter) params.city = cityFilter;
      if (minAgents) params.min_agents = minAgents;
      const res = await axiosInstance.get('/broker/admin/market-analytics', { params });
      if (res.data?.success) {
        setOffices(res.data.offices || []);
        setStats(res.data.stats || {});
        if (res.data.insights) setInsights(res.data.insights);
      }
    } catch (err) {
      console.error('Failed to load market analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [search, cityFilter, minAgents]);

  const fetchMembers = useCallback(async (sparkId: string) => {
    setMembersLoading(true);
    try {
      const res = await axiosInstance.get(`/broker/admin/market-analytics/office/${sparkId}/members`);
      if (res.data?.success) setMembers(res.data.members || []);
    } catch { setMembers([]); }
    finally { setMembersLoading(false); }
  }, []);

  useEffect(() => { fetchOffices(); }, [fetchOffices]);

  const fetchAgentSearch = useCallback(async (q: string) => {
    if (!q || q.length < 2) { setAgentSearchResults([]); return; }
    setAgentSearchLoading(true);
    try {
      const res = await axiosInstance.get('/broker/admin/market-analytics/agents', { params: { search: q } });
      if (res.data?.success) setAgentSearchResults(res.data.agents || []);
    } catch { setAgentSearchResults([]); }
    finally { setAgentSearchLoading(false); }
  }, []);

  // Load agent from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const agentMlsId = params.get('agentId');
    if (agentMlsId) {
      setSearchMode('agents');
      axiosInstance.get('/broker/admin/market-analytics/agents', { params: { search: agentMlsId } }).then(r => {
        if (r.data?.success && r.data.agents?.length > 0) {
          const agent = r.data.agents.find((a: any) => a.short_id === agentMlsId) || r.data.agents[0];
          openAgentDetail(agent);
        }
      }).catch(() => {});
    }
  }, []);

  const openAgentDetail = useCallback(async (agent: any) => {
    setAgentDetail(agent);
    setAgentProduction(null);
    setAgentDetailTab('production');
    setAgentRetention(null);
    setAgentLostDealsDetail([]);
    setAgentProdLoading(true);
    // Update URL
    const url = new URL(window.location.href);
    url.searchParams.set('agentId', agent.short_id);
    window.history.replaceState({}, '', url.toString());
    try {
      const res = await axiosInstance.get(`/broker/admin/market-analytics/agents/${agent.short_id}/production`);
      if (res.data?.success) setAgentProduction(res.data.production);
    } catch { setAgentProduction(null); }
    finally { setAgentProdLoading(false); }
  }, []);

  const downloadAgentReport = useCallback(async (agent: any) => {
    setAgentReportLoading(true);
    try {
      const res = await axiosInstance.get(`/broker/admin/market-analytics/agents/${agent.short_id}/report`, { responseType: 'blob' });
      const ct = res.headers['content-type'] || '';
      const ext = ct.includes('pdf') ? 'pdf' : 'docx';
      const blob = new Blob([res.data], { type: ct });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(agent.member_name || 'Agent').replace(/[^a-zA-Z0-9]/g, '_')}_Production_Report.${ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) { console.error('Report failed:', err); window.alert('Report generation failed.'); }
    finally { setAgentReportLoading(false); }
  }, []);
  const handleInviteAgent = useCallback(async (agent: MLSMember) => {
    if (!agent.email || invitingAgent) return;
    setInvitingAgent(agent.id);
    try {
      await axiosInstance.post('/lender/send-sponsor-invite', {
        agentEmail: agent.email,
        agentFirstName: agent.first_name || agent.member_name?.split(' ')[0] || '',
        sponsorCompany: selected?.office_name || 'KairosIQ',
        sponsorType: 'brokerage',
        sponsorContactName: selected?.email || '',
      });
      setInvitedAgents(prev => new Set(prev).add(agent.id));
    } catch (err) {
      console.error('Invite failed:', err);
      window.alert('Failed to send invitation');
    } finally {
      setInvitingAgent(null);
    }
  }, [selected, invitingAgent]);

  const fetchPerformance = useCallback(async (shortId: string) => {
    setPerfLoading(true);
    try {
      const res = await axiosInstance.get(`/broker/admin/market-analytics/office/${shortId}/performance`);
      if (res.data?.success) setPerformance(res.data.performance);
    } catch { setPerformance(null); }
    finally { setPerfLoading(false); }
  }, []);

  useEffect(() => {
    if (selected?.spark_id) fetchMembers(selected.spark_id);
    else setMembers([]);
    if (selected?.short_id) fetchPerformance(selected.short_id);
    else setPerformance(null);
    setDetailTab('roster');
    setRetentionData(null);
  }, [selected?.spark_id, selected?.short_id, fetchMembers, fetchPerformance]);

  return (
    <div style={{ minHeight: '100vh', background: colors.navy, fontFamily: fonts.body }}>
      <link href={FONT_LINK} rel="stylesheet" />

      {/* Nav */}
      <div style={{ background: colors.navy3, borderBottom: `2px solid ${colors.gold}`, padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontFamily: fonts.display, fontSize: '20px', color: colors.white, letterSpacing: '2px' }}>
            AI<span style={{ color: colors.gold2 }}>Search</span>Portal
          </span>
          <span style={{ color: colors.muted, fontSize: '12px' }}>/ Admin / Market Analytics</span>
        </div>
        <span style={{ fontSize: '10px', color: colors.muted }}>
          <FontAwesomeIcon icon={faDatabase} style={{ marginRight: '4px' }} />
          kairos_market_analytics
        </span>
      </div>

      {/* Stats strip */}
      <div style={{ background: colors.navy2, borderBottom: `1px solid ${colors.navy3}`, padding: '14px 24px', display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
        <StatChip icon={faBuilding} label="Offices" value={stats.total_offices.toLocaleString()} color={colors.gold} />
        <StatChip icon={faUsers} label="Agents" value={stats.total_agents.toLocaleString()} color={colors.blue} />
        <StatChip icon={faMapMarkerAlt} label="Cities" value={stats.cities} color={colors.green} />
        <StatChip icon={faGlobe} label="Boards" value={stats.boards} color={colors.muted} />
      </div>

      {/* Split panel */}
      <div style={{ display: 'flex', height: 'calc(100vh - 105px)' }}>
        {/* Left */}
        <div style={{ width: leftPanelCollapsed ? '40px' : '420px', flexShrink: 0, background: colors.navy2, borderRight: `1px solid ${colors.navy3}`, display: 'flex', flexDirection: 'column', transition: 'width 0.2s ease', overflow: 'hidden', position: 'relative' }}>
          {/* Collapse toggle */}
          <button onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
            style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 10, background: colors.navy3, color: colors.muted, border: 'none', borderRadius: '4px', width: '24px', height: '24px', cursor: 'pointer', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {leftPanelCollapsed ? '▶' : '◀'}
          </button>
          {leftPanelCollapsed ? <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', padding: '40px 8px', color: colors.muted, fontSize: '11px', fontWeight: 600, letterSpacing: '1px' }}>{searchMode === 'offices' ? 'OFFICES' : 'AGENTS'}</div> : <>
          {/* Search Mode Toggle + Filters */}
          <div style={{ padding: '12px 14px', borderBottom: `1px solid ${colors.navy3}` }}>
            <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
              {(['offices', 'agents'] as const).map(mode => (
                <button key={mode} onClick={() => { setSearchMode(mode); setAgentDetail(null); setAgentSearchResults([]); }}
                  style={{ flex: 1, background: searchMode === mode ? colors.gold : colors.navy3, color: searchMode === mode ? colors.navy : colors.muted, border: 'none', borderRadius: '6px', padding: '6px 10px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', textTransform: 'capitalize' }}>
                  {mode === 'offices' ? '🏢 Offices' : '👤 Agents'}
                </button>
              ))}
            </div>
            <div style={{ position: 'relative', marginBottom: '8px' }}>
              <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '10px', top: '9px', fontSize: '11px', color: colors.muted }} />
              <input
                value={search} onChange={(e) => { setSearch(e.target.value); if (searchMode === 'agents') fetchAgentSearch(e.target.value); }}
                placeholder={searchMode === 'offices' ? "Search offices…" : "Search agents by name, email, or license…"}
                style={{ width: '100%', background: colors.navy3, color: colors.white, border: 'none', borderRadius: '6px', padding: '8px 10px 8px 30px', fontSize: '12px', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <input
                value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}
                placeholder="City filter"
                style={{ flex: 1, background: colors.navy3, color: colors.white, border: 'none', borderRadius: '6px', padding: '6px 10px', fontSize: '11px', outline: 'none' }}
              />
              <input
                value={minAgents} onChange={(e) => setMinAgents(e.target.value)}
                placeholder="Min agents"
                type="number"
                style={{ width: '80px', background: colors.navy3, color: colors.white, border: 'none', borderRadius: '6px', padding: '6px 10px', fontSize: '11px', outline: 'none' }}
              />
            </div>
          </div>

          {/* Office list OR Agent search results */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {searchMode === 'agents' ? (
              /* Agent search results */
              agentSearchLoading ? (
                <div style={{ padding: '32px', textAlign: 'center', color: colors.muted, fontSize: '12px' }}>Searching agents…</div>
              ) : agentSearchResults.length === 0 ? (
                <div style={{ padding: '32px', textAlign: 'center', color: colors.muted, fontSize: '12px' }}>{search.length >= 2 ? 'No agents found' : 'Type at least 2 characters to search'}</div>
              ) : (
                agentSearchResults.map((a: any) => {
                  const isSel = agentDetail?.short_id === a.short_id;
                  return (
                    <div key={a.id} onClick={() => { openAgentDetail(a); setSelected(null); }}
                      style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${colors.navy3}`, borderLeft: isSel ? `3px solid ${colors.gold}` : '3px solid transparent', background: isSel ? colors.navy3 : 'transparent' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {a.photo_url ? (
                          <img src={a.photo_url} alt="" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: `${colors.gold}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: colors.gold, fontWeight: 700 }}>{(a.first_name||'?')[0]}</div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: colors.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.member_name || '—'}</div>
                          <div style={{ fontSize: '10px', color: colors.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.office_name || ''} · {a.local_type || 'Agent'}</div>
                        </div>
                        {a.is_kairosiq_customer ? <span style={{ fontSize: '8px', fontWeight: 700, padding: '2px 5px', borderRadius: '3px', background: `${colors.green}33`, color: colors.green }}>KIQ</span> : null}
                      </div>
                    </div>
                  );
                })
              )
            ) : (
            /* Office list */
            loading ? (
              <div style={{ padding: '32px', textAlign: 'center', color: colors.muted, fontSize: '12px' }}>Loading market data…</div>
            ) : offices.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: colors.muted, fontSize: '12px' }}>No offices match your filters</div>
            ) : (
              offices.map((o) => {
                const isSel = selected?.id === o.id;
                return (
                  <div key={o.id} onClick={() => { setSelected(o); setAgentDetail(null); }}
                    style={{ padding: '12px 14px', cursor: 'pointer', borderBottom: `1px solid ${colors.navy3}`, borderLeft: isSel ? `3px solid ${colors.gold}` : '3px solid transparent', background: isSel ? colors.navy3 : 'transparent' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: '12px', fontWeight: 700, color: colors.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '280px' }}>
                        {o.office_name || o.short_id || '—'}
                      </div>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: colors.gold, flexShrink: 0 }}>
                        {o.agent_count}
                      </span>
                    </div>
                    <div style={{ fontSize: '10px', color: colors.muted, marginTop: '2px' }}>
                      {o.city && o.state ? `${o.city}, ${o.state}` : '—'}
                      {o.board ? ` · ${o.board}` : ''}
                      {o.short_id ? ` · ${o.short_id}` : ''}
                    </div>
                  </div>
                );
              })
            )
            )}
          </div>
          </>}
        </div>

        {/* Right */}
        <div style={{ flex: 1, overflowY: 'auto', background: colors.offBg }}>
          {/* Agent Detail Panel */}
          {agentDetail ? (
            <div style={{ padding: '24px' }}>
              {/* Agent Header */}
              <div style={{ background: '#f8f9fa', borderLeft: `4px solid ${colors.gold}`, borderRadius: '8px', padding: '20px 24px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                {agentDetail.photo_url ? (
                  <img src={agentDetail.photo_url} alt="" style={{ width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: `${colors.navy}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', color: colors.muted, fontWeight: 700 }}>{(agentDetail.first_name||'?')[0]}</div>
                )}
                <div>
                  <h2 style={{ fontFamily: fonts.display, fontSize: '26px', color: colors.navy, margin: '0 0 4px' }}>{agentDetail.member_name}</h2>
                  <div style={{ fontSize: '12px', color: colors.muted }}>{agentDetail.office_name} · {agentDetail.local_type || 'Agent'}</div>
                  <div style={{ fontSize: '11px', color: colors.muted }}>MLS ID: <strong>{agentDetail.short_id}</strong> · License: {agentDetail.license_number || '—'} · {agentDetail.email || ''} · {agentDetail.phone || ''}</div>
                  {agentDetail.id && <div style={{ fontSize: '10px', color: '#ccc', marginTop: '2px' }}>Member ID: {agentDetail.id}</div>}
                </div>
              </div>

              {/* Generate Report + Back buttons */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button onClick={() => { setAgentDetail(null); const u = new URL(window.location.href); u.searchParams.delete('agentId'); window.history.replaceState({}, '', u.toString()); }}
                  style={{ background: colors.muted, color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                  ← Back
                </button>
                <button onClick={() => downloadAgentReport(agentDetail)} disabled={agentReportLoading}
                  style={{ background: agentReportLoading ? colors.muted : `linear-gradient(135deg, ${colors.gold}, #E8B44A)`, color: '#0F1E35', border: 'none', borderRadius: '8px', padding: '8px 20px', fontSize: '12px', fontWeight: 700, cursor: agentReportLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FontAwesomeIcon icon={faChartLine} />
                  {agentReportLoading ? 'Generating…' : 'Brokerage Recruitment Report'}
                </button>
                <button onClick={async () => {
                  try {
                    const btn = document.getElementById('platform-report-btn') as HTMLButtonElement;
                    if (btn) { btn.disabled = true; btn.textContent = 'Generating…'; }
                    const res = await axiosInstance.get(`/broker/admin/market-analytics/agents/${agentDetail.short_id}/platform-report`, { responseType: 'blob' });
                    const ct = res.headers['content-type'] || '';
                    const ext = ct.includes('pdf') ? 'pdf' : 'docx';
                    const blob = new Blob([res.data], { type: ct });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${(agentDetail.member_name || 'Agent').replace(/[^a-zA-Z0-9]/g, '_')}_Platform_Report.${ext}`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                  } catch { window.alert('Report generation failed.'); }
                  finally { const btn = document.getElementById('platform-report-btn') as HTMLButtonElement; if (btn) { btn.disabled = false; btn.textContent = 'AI Platform Value Report'; } }
                }} id="platform-report-btn"
                  style={{ background: `linear-gradient(135deg, #0F1E35, ${colors.navy})`, color: colors.gold, border: `1px solid ${colors.gold}`, borderRadius: '8px', padding: '8px 20px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FontAwesomeIcon icon={faFilePdf} />
                  AI Platform Value Report
                </button>
              </div>

              {/* Agent Detail Tabs */}
              <div style={{ display: 'flex', gap: 0, borderBottom: `2px solid ${colors.border}`, marginBottom: '14px' }}>
                {(['production', 'retention'] as const).map(tab => (
                  <button key={tab} onClick={() => {
                    setAgentDetailTab(tab);
                    if (tab === 'retention' && !agentRetention && agentDetail?.short_id) {
                      setAgentRetentionLoading(true);
                      axiosInstance.get(`/broker/retention/agent/${agentDetail.short_id}`).then(r => {
                        if (r.data.success) setAgentRetention(r.data);
                      }).catch(() => {}).finally(() => setAgentRetentionLoading(false));
                    }
                  }}
                  style={{ background: 'none', border: 'none', borderBottom: `2px solid ${agentDetailTab === tab ? colors.gold : 'transparent'}`, color: agentDetailTab === tab ? colors.navy : colors.muted, fontWeight: agentDetailTab === tab ? 700 : 500, fontSize: '12px', padding: '10px 18px', cursor: 'pointer', marginBottom: '-2px', fontFamily: 'inherit', textTransform: 'capitalize' }}>
                    {tab === 'production' ? `Production${agentProduction ? ` (${agentProduction.total_deals})` : ''}` : `Retention Analysis${agentRetention ? ` (${agentRetention.lost || 0} lost)` : ''}`}
                  </button>
                ))}
              </div>

              {/* Production Tab */}
              {agentDetailTab === 'production' && (
              <>
              {agentProdLoading ? (
                <div style={{ padding: '32px', textAlign: 'center', color: colors.muted }}>Loading production data…</div>
              ) : agentProduction ? (
                <div>
                  {/* KPIs */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '8px', marginBottom: '14px' }}>
                    {[
                      { label: 'Total Deals', value: agentProduction.total_deals.toLocaleString(), color: colors.gold },
                      { label: 'Total Volume', value: `$${(agentProduction.total_volume / 1000000).toFixed(1)}M`, color: colors.green },
                      { label: 'Avg Price', value: `$${agentProduction.avg_price.toLocaleString()}`, color: colors.blue },
                      { label: 'YTD Deals', value: agentProduction.ytd_deals, color: colors.orange },
                      { label: 'YTD Volume', value: `$${(agentProduction.ytd_volume / 1000000).toFixed(1)}M`, color: colors.green },
                      { label: 'Avg DOM', value: `${agentProduction.avg_dom}d`, color: colors.muted },
                    ].map((s: any, i: number) => (
                      <div key={i} style={{ background: 'white', borderLeft: `3px solid ${s.color}`, borderRadius: '6px', padding: '10px 12px', border: '1px solid #eee' }}>
                        <div style={{ fontSize: '9px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
                        <div style={{ fontSize: '16px', fontWeight: 700, color: '#333', marginTop: '2px' }}>{s.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Listing vs Buyer */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '14px' }}>
                    <div style={{ background: '#f0f7ff', borderRadius: '6px', padding: '10px 14px', border: '1px solid #d0e4f7' }}>
                      <div style={{ fontSize: '10px', color: colors.blue, textTransform: 'uppercase', fontWeight: 700 }}>Listing Side</div>
                      <div style={{ fontSize: '18px', fontWeight: 700, color: '#333' }}>{agentProduction.listing_deals} deals</div>
                    </div>
                    <div style={{ background: '#f0faf0', borderRadius: '6px', padding: '10px 14px', border: '1px solid #d0e8d0' }}>
                      <div style={{ fontSize: '10px', color: colors.green, textTransform: 'uppercase', fontWeight: 700 }}>Buyer Side</div>
                      <div style={{ fontSize: '18px', fontWeight: 700, color: '#333' }}>{agentProduction.buyer_deals} deals</div>
                    </div>
                  </div>

                  {/* By Year */}
                  {agentProduction.by_year?.length > 0 && (
                    <div style={{ background: 'white', borderRadius: '8px', padding: '14px', border: '1px solid #eee', marginBottom: '14px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Deals by Year</div>
                      {agentProduction.by_year.map((y: any, i: number) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #eee', fontSize: '12px' }}>
                          <span style={{ fontWeight: 600 }}>{y.year}</span>
                          <span style={{ color: '#888' }}>{y.deals} deals · <strong style={{ color: colors.gold }}>${(y.volume / 1000000).toFixed(1)}M</strong></span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Recent Deals */}
                  {agentProduction.recent_deals?.length > 0 && (
                    <div style={{ background: 'white', borderRadius: '8px', padding: '14px', border: '1px solid #eee' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Recent Deals</div>
                      {agentProduction.recent_deals.map((d: any, i: number) => (
                        <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid #eee', fontSize: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ fontWeight: 500, color: '#333', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.address || '—'}</span>
                            <span style={{ fontWeight: 700, color: colors.gold }}>${d.price?.toLocaleString()}</span>
                          </div>
                          <div style={{ color: '#999', fontSize: '11px', marginTop: '2px' }}>
                            {d.date ? new Date(d.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}
                            {d.beds ? ` · ${d.beds}bd/${d.baths}ba` : ''}{d.sqft ? ` · ${d.sqft.toLocaleString()} sqft` : ''}{d.dom ? ` · ${d.dom}d DOM` : ''}
                            <span style={{ marginLeft: '6px', fontSize: '9px', fontWeight: 700, padding: '1px 5px', borderRadius: '3px', background: d.side === 'listing' ? '#e8f4fd' : '#e8fde8', color: d.side === 'listing' ? colors.blue : colors.green, textTransform: 'uppercase' }}>{d.side}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ padding: '32px', textAlign: 'center', color: colors.muted }}>No production data available</div>
              )}
              </>
              )}

              {/* Retention Tab */}
              {agentDetailTab === 'retention' && (
                <div>
                  {agentRetentionLoading ? (
                    <div style={{ padding: '32px', textAlign: 'center', color: colors.muted }}>Loading retention analysis…</div>
                  ) : !agentRetention ? (
                    <div style={{ padding: '32px', textAlign: 'center', color: colors.muted }}>No retention data available for this agent.</div>
                  ) : (
                    <div>
                      {/* Retention KPIs */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '8px', marginBottom: '14px' }}>
                        {[
                          { label: 'Total Buyer Deals', value: agentRetention.total_buyer_deals || 0, color: colors.blue },
                          { label: 'Retained', value: agentRetention.retained || 0, color: colors.green },
                          { label: 'Lost', value: agentRetention.lost || 0, color: colors.red },
                          { label: 'Retention Rate', value: `${Number(agentRetention.retention_rate || 0)}%`, color: (agentRetention.retention_rate || 0) > 50 ? colors.green : colors.red },
                          { label: 'Lost Revenue', value: `$${(Number(agentRetention.lost_revenue || 0) / 1000).toFixed(0)}K`, color: colors.red },
                        ].map((s: any, i: number) => (
                          <div key={i} style={{ background: 'white', borderLeft: `3px solid ${s.color}`, borderRadius: '6px', padding: '10px 12px', border: '1px solid #eee' }}>
                            <div style={{ fontSize: '9px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
                            <div style={{ fontSize: '16px', fontWeight: 700, color: '#333', marginTop: '2px' }}>{s.value}</div>
                          </div>
                        ))}
                      </div>

                      {/* Lost Deals List */}
                      {agentRetention.lost_deals?.length > 0 && (
                        <div style={{ background: 'white', borderRadius: '8px', padding: '14px', border: '1px solid #eee' }}>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: colors.red, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Lost Deals — Past Buyers Who Listed With a Different Agent ({agentRetention.lost_deals.length})</div>
                          {agentRetention.lost_deals.slice(0, 20).map((d: any, i: number) => (
                            <div key={i} style={{ padding: '10px 0', borderBottom: '1px solid #eee' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                  <span style={{ fontWeight: 600, color: '#333', fontSize: '13px' }}>{d.address || '—'}</span>
                                  {d.city && <span style={{ color: colors.muted, fontSize: '11px', marginLeft: '8px' }}>{d.city}</span>}
                                </div>
                                <span style={{ fontWeight: 700, color: colors.red, fontSize: '14px' }}>${Number(d.resale_price || 0).toLocaleString()}</span>
                              </div>
                              <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                                Bought: {d.purchase_date ? new Date(d.purchase_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'} for ${Number(d.purchase_price || 0).toLocaleString()}
                                {' → '}Listed: {d.resale_date ? new Date(d.resale_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}
                                {d.listing_agent_id && <span> · Lost to: <strong style={{ color: colors.red }}>{d.listing_agent_id}</strong></span>}
                              </div>
                            </div>
                          ))}
                          {agentRetention.lost_deals.length > 20 && <div style={{ padding: '8px 0', color: colors.muted, fontSize: '11px', textAlign: 'center' }}>+ {agentRetention.lost_deals.length - 20} more</div>}
                        </div>
                      )}

                      {/* Retention insight */}
                      {(agentRetention.lost || 0) > 0 && (
                        <div style={{ background: '#fff5f5', borderLeft: `4px solid ${colors.red}`, borderRadius: '4px', padding: '14px 18px', marginTop: '14px' }}>
                          <div style={{ fontWeight: 700, color: colors.red, fontSize: '13px', marginBottom: '4px' }}>Revenue Impact</div>
                          <div style={{ color: '#555', fontSize: '13px' }}>
                            {agentRetention.lost} past buyer{agentRetention.lost > 1 ? 's' : ''} listed with a different agent — ${Number(agentRetention.lost_revenue || 0).toLocaleString()} in potential commission lost.
                            KairosIQ monitors every past client 24/7 and alerts the agent before they list with someone else.
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : selected ? (
            <div style={{ padding: '24px' }}>
              {/* Header */}
              <div style={{ background: '#f8f9fa', borderLeft: `4px solid ${colors.gold}`, borderRadius: '8px', padding: '20px 24px', marginBottom: '16px' }}>
                <h2 style={{ fontFamily: fonts.display, fontSize: '26px', color: colors.navy, margin: '0 0 6px', letterSpacing: '0.5px' }}>
                  {selected.office_name || '—'}
                </h2>
                <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', fontSize: '12px', color: colors.muted }}>
                  {selected.full_address && <span><FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: '4px' }} />{selected.full_address}</span>}
                  {selected.phone && <span><FontAwesomeIcon icon={faPhone} style={{ marginRight: '4px' }} />{selected.phone}</span>}
                  {selected.email && <span><FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '4px' }} />{selected.email}</span>}
                </div>
                <div style={{ display: 'flex', gap: '12px', marginTop: '8px', fontSize: '11px', color: colors.muted }}>
                  {selected.license_number && <span><FontAwesomeIcon icon={faIdCard} style={{ marginRight: '4px' }} />License: {selected.license_number}</span>}
                  {selected.short_id && <span>Office ID: {selected.short_id}</span>}
                  {selected.broker_short_id && <span>Broker ID: {selected.broker_short_id}</span>}
                  {selected.board && <span>Board: {selected.board}</span>}
                </div>
              </div>

              {/* Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                <MiniStat label="Agents" value={selected.agent_count} color={colors.blue} />
                <MiniStat label="MLS" value={selected.mls_name || '—'} color={colors.gold} />
                <MiniStat label="Board" value={selected.board || '—'} color={colors.muted} />
                <MiniStat label="Status" value={selected.is_active ? 'Active' : 'Inactive'} color={selected.is_active ? colors.green : colors.red} />
              </div>

              {/* Generate Report Button */}
              {selected.short_id && (
                <div style={{ marginBottom: '14px' }}>
                  <button
                    onClick={async () => {
                      if (!selected.short_id) return;
                      setReportLoading(true);
                      try {
                        const res = await axiosInstance.get(
                          `/broker/admin/market-analytics/office/${selected.short_id}/report`,
                          { responseType: 'blob' }
                        );
                        const contentType = res.headers['content-type'] || '';
                        const ext = contentType.includes('pdf') ? 'pdf' : 'docx';
                        const blob = new Blob([res.data], { type: contentType });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${(selected.office_name || 'Brokerage').replace(/[^a-zA-Z0-9]/g, '_')}_Production_Report.${ext}`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      } catch (err) {
                        console.error('Report generation failed:', err);
                        window.alert('Report generation failed. Please try again.');
                      } finally {
                        setReportLoading(false);
                      }
                    }}
                    disabled={reportLoading}
                    style={{
                      background: reportLoading ? colors.muted : `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                      color: '#0F1E35', border: 'none', borderRadius: '8px',
                      padding: '10px 20px', fontSize: '13px', fontWeight: 700,
                      cursor: reportLoading ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', gap: '8px',
                      fontFamily: 'inherit',
                    }}
                  >
                    <FontAwesomeIcon icon={faFilePdf} />
                    {reportLoading ? 'Generating Report…' : 'Generate Production Report (PDF)'}
                  </button>
                </div>
              )}

              {/* Detail tabs */}
              <div style={{ display: 'flex', gap: 0, borderBottom: `2px solid ${colors.border}`, marginBottom: '14px' }}>
                {[
                  { key: 'roster' as const, label: `Agent Roster (${members.length})` },
                  { key: 'performance' as const, label: `Sales Performance${performance?.overview?.total_deals ? ` (${performance.overview.total_deals.toLocaleString()})` : ''}` },
                  { key: 'retention' as const, label: `Retention Analysis${retentionData ? ` (${retentionData.report?.loss_rate || 0}% lost)` : ''}` },
                ].map((tab) => (
                  <button key={tab.key} onClick={() => {
                    setDetailTab(tab.key);
                    if (tab.key === 'retention' && !retentionData && selected) {
                      setRetentionLoading(true);
                      axiosInstance.get(`/broker/retention/reports/${selected.short_id}`).then(r => {
                        if (r.data.success && r.data.report) { setRetentionData(r.data); setRetentionLoading(false); }
                        else throw new Error('not found');
                      }).catch(() => {
                        axiosInstance.get(`/broker/retention/reports?search=${encodeURIComponent(selected.office_name || '')}&limit=1`).then(r => {
                          if (r.data.success && r.data.reports?.length > 0) {
                            axiosInstance.get(`/broker/retention/reports/${r.data.reports[0].office_short_id}`).then(r2 => {
                              setRetentionData(r2.data.success ? r2.data : null);
                            }).catch(() => setRetentionData(null));
                          } else setRetentionData(null);
                        }).catch(() => setRetentionData(null)).finally(() => setRetentionLoading(false));
                      });
                    }
                  }}
                    style={{ background: 'none', border: 'none', borderBottom: `2px solid ${detailTab === tab.key ? colors.gold : 'transparent'}`, color: detailTab === tab.key ? colors.navy : colors.muted, fontWeight: detailTab === tab.key ? 700 : 500, fontSize: '12px', padding: '10px 18px', cursor: 'pointer', marginBottom: '-2px', fontFamily: 'inherit' }}>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Performance tab */}
              {detailTab === 'performance' && (
                <div>
                  {perfLoading ? (
                    <div style={{ padding: '32px', textAlign: 'center', color: colors.muted, fontSize: '12px' }}>Loading performance data…</div>
                  ) : !performance?.overview ? (
                    <div style={{ padding: '32px', textAlign: 'center', color: colors.muted, fontSize: '12px' }}>No sales data found for this office</div>
                  ) : (
                    <>
                      {/* Overview KPIs */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '14px' }}>
                        <MiniStat label="Total Deals" value={performance.overview.total_deals.toLocaleString()} color={colors.gold} />
                        <MiniStat label="Total Volume" value={`$${(performance.overview.total_volume / 1000000).toFixed(1)}M`} color={colors.green} />
                        <MiniStat label="Avg Sale Price" value={`$${performance.overview.avg_sale_price.toLocaleString()}`} color={colors.blue} />
                        <MiniStat label="Avg DOM" value={`${performance.overview.avg_dom}d`} color={colors.orange} />
                        <MiniStat label="Avg $/sqft" value={`$${performance.overview.avg_ppsf}`} color={colors.navy} />
                        <MiniStat label="SP/LP Ratio" value={`${((performance.overview.avg_sp_lp_ratio || 0) * 100).toFixed(1)}%`} color={colors.muted} />
                      </div>

                      {/* Listing vs Buyer split */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                        <div style={{ background: colors.white, border: `1px solid ${colors.border}`, borderLeft: `3px solid ${colors.blue}`, borderRadius: '6px', padding: '12px 14px' }}>
                          <div style={{ fontSize: '10px', color: colors.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Listing Side</div>
                          <div style={{ fontSize: '18px', fontWeight: 700, color: colors.navy }}>{performance.overview.listing_deals.toLocaleString()} deals</div>
                          <div style={{ fontSize: '12px', color: colors.muted }}>${(performance.overview.listing_volume / 1000000).toFixed(1)}M volume</div>
                        </div>
                        <div style={{ background: colors.white, border: `1px solid ${colors.border}`, borderLeft: `3px solid ${colors.green}`, borderRadius: '6px', padding: '12px 14px' }}>
                          <div style={{ fontSize: '10px', color: colors.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Buyer Side</div>
                          <div style={{ fontSize: '18px', fontWeight: 700, color: colors.navy }}>{performance.overview.buyer_deals.toLocaleString()} deals</div>
                          <div style={{ fontSize: '12px', color: colors.muted }}>${(performance.overview.buyer_volume / 1000000).toFixed(1)}M volume</div>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                        {/* Deals by year */}
                        <Card title="Deals by Year">
                          {(performance.by_year || []).map((y: any, i: number) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${colors.border}`, fontSize: '12px' }}>
                              <span style={{ fontWeight: 600, color: colors.navy }}>{y.year}</span>
                              <span style={{ color: colors.muted }}>
                                {y.deals} deals · <strong style={{ color: colors.gold }}>${(y.volume / 1000000).toFixed(1)}M</strong> · avg ${y.avg_price.toLocaleString()}
                              </span>
                            </div>
                          ))}
                        </Card>

                        {/* Top listing agents */}
                        <Card title="Top Listing Agents">
                          {(performance.top_list_agents || []).map((a: any, i: number) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${colors.border}`, fontSize: '12px' }}>
                              <span style={{ fontWeight: 600, color: colors.navy }}>{i + 1}. {a.name} <span style={{ color: colors.muted, fontSize: '10px' }}>({a.mls_id})</span></span>
                              <span style={{ color: colors.muted }}>{a.deals} deals · <strong style={{ color: colors.gold }}>${(a.volume / 1000000).toFixed(1)}M</strong></span>
                            </div>
                          ))}
                        </Card>
                      </div>

                      {/* Top buyer agents */}
                      <Card title="Top Buyer Agents">
                        {(performance.top_buyer_agents || []).map((a: any, i: number) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${colors.border}`, fontSize: '12px' }}>
                            <span style={{ fontWeight: 600, color: colors.navy }}>{i + 1}. {a.name} <span style={{ color: colors.muted, fontSize: '10px' }}>({a.mls_id})</span></span>
                            <span style={{ color: colors.muted }}>{a.deals} deals · <strong style={{ color: colors.gold }}>${(a.volume / 1000000).toFixed(1)}M</strong></span>
                          </div>
                        ))}
                      </Card>

                      {/* Recent deals */}
                      <div style={{ marginTop: '14px' }}>
                        <Card title="Recent Closed Deals">
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                              <thead>
                                <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                                  <Th>Address</Th>
                                  <Th>Price</Th>
                                  <Th>Date</Th>
                                  <Th>DOM</Th>
                                  <Th>Side</Th>
                                  <Th>Agent</Th>
                                </tr>
                              </thead>
                              <tbody>
                                {(performance.recent_deals || []).map((d: any, i: number) => (
                                  <tr key={i} style={{ borderBottom: `1px solid ${colors.border}`, background: i % 2 === 0 ? 'transparent' : '#f9fafb' }}>
                                    <Td>
                                      <div style={{ fontWeight: 500, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.address || '—'}</div>
                                      <div style={{ fontSize: '10px', color: colors.muted }}>{d.beds}bd/{d.baths}ba · {d.sqft?.toLocaleString() || '—'} sqft</div>
                                    </Td>
                                    <Td><strong>${d.price?.toLocaleString()}</strong></Td>
                                    <Td>{d.date ? new Date(d.date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) : '—'}</Td>
                                    <Td>{d.dom || '—'}</Td>
                                    <Td>
                                      <span style={{ fontSize: '9px', fontWeight: 700, padding: '2px 6px', borderRadius: '3px', background: d.side === 'listing' ? `${colors.blue}22` : `${colors.green}22`, color: d.side === 'listing' ? colors.blue : colors.green, textTransform: 'uppercase' }}>
                                        {d.side}
                                      </span>
                                    </Td>
                                    <Td>{d.side === 'listing' ? d.list_agent : d.buyer_agent}</Td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </Card>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Members table */}
              {detailTab === 'roster' && (
              <div style={{ background: colors.white, border: `1px solid ${colors.border}`, borderLeft: `4px solid ${colors.gold}`, borderRadius: '10px' }}>
                <div style={{ padding: '14px 20px', borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FontAwesomeIcon icon={faUsers} style={{ color: colors.gold, fontSize: '14px' }} />
                  <h5 style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: '1px' }}>
                    Agent Roster ({members.length})
                  </h5>
                </div>
                <div style={{ padding: '12px 16px' }}>
                  {membersLoading ? (
                    <div style={{ color: colors.muted, fontSize: '12px', padding: '16px', textAlign: 'center' }}>Loading agents…</div>
                  ) : members.length === 0 ? (
                    <div style={{ color: colors.muted, fontSize: '12px', padding: '16px', textAlign: 'center' }}>No agents found</div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                          <Th>Agent</Th>
                          <Th>Email</Th>
                          <Th>Phone</Th>
                          <Th>License</Th>
                          <Th>Type</Th>
                          <Th>Customer?</Th>
                          <Th>Action</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {members.map((m, idx) => (
                          <tr key={m.id} style={{ borderBottom: `1px solid ${colors.border}`, background: idx % 2 === 0 ? 'transparent' : '#f9fafb' }}>
                            <Td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {m.photo_url ? (
                                  <img src={m.photo_url} alt="" style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover' }} />
                                ) : (
                                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: `${colors.navy}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: colors.muted }}>
                                    {(m.first_name || '?')[0]}
                                  </div>
                                )}
                                <span onClick={(e) => { e.stopPropagation(); openAgentDetail({ ...m, short_id: (m as any).short_id }); }} style={{ fontWeight: 600, color: colors.navy, cursor: 'pointer', textDecoration: 'underline', textDecorationColor: colors.gold }}>{m.member_name || '—'}</span>
                              </div>
                            </Td>
                            <Td>{m.email || '—'}</Td>
                            <Td>{m.phone || '—'}</Td>
                            <Td>{m.license_number || '—'}</Td>
                            <Td>{m.local_type || '—'}</Td>
                            <Td>
                              {m.is_kairosiq_customer ? (
                                <span style={{ background: `${colors.green}22`, color: colors.green, fontSize: '9px', fontWeight: 700, padding: '2px 6px', borderRadius: '4px' }}>YES</span>
                              ) : (
                                <span style={{ background: `${colors.muted}22`, color: colors.muted, fontSize: '9px', fontWeight: 700, padding: '2px 6px', borderRadius: '4px' }}>NO</span>
                              )}
                            </Td>
                            <Td>
                              {m.is_kairosiq_customer ? (
                                <span style={{ fontSize: '9px', color: colors.green }}>Active</span>
                              ) : !m.email ? (
                                <span style={{ fontSize: '9px', color: colors.muted }}>No email</span>
                              ) : invitedAgents.has(m.id) ? (
                                <span style={{ fontSize: '9px', color: colors.green, fontWeight: 600 }}>Sent</span>
                              ) : (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleInviteAgent(m); }}
                                  disabled={invitingAgent === m.id}
                                  style={{
                                    background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                                    border: 'none', borderRadius: '4px', padding: '3px 10px',
                                    fontSize: '9px', fontWeight: 700, color: colors.navy,
                                    cursor: 'pointer', whiteSpace: 'nowrap',
                                  }}
                                >
                                  {invitingAgent === m.id ? 'Sending…' : 'Invite'}
                                </button>
                              )}
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
              )}
            </div>
          ) : (
            <div style={{ padding: '24px', overflowY: 'auto', height: '100%' }}>
              {/* KPI row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                <MiniStat label="Total Offices" value={stats.total_offices.toLocaleString()} color={colors.gold} />
                <MiniStat label="Total Agents" value={stats.total_agents.toLocaleString()} color={colors.blue} />
                <MiniStat label="Avg Office Size" value={stats.avg_office_size} color={colors.green} />
                <MiniStat label="Largest Office" value={stats.largest_office.toLocaleString()} color={colors.red} />
                <MiniStat label="Cities" value={stats.cities} color={colors.muted} />
                <MiniStat label="Boards" value={stats.boards} color={colors.orange} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                {/* Top cities */}
                <Card title="Top Markets by Agent Count">
                  {(insights.top_cities || []).map((c: any, i: number) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: i < 9 ? `1px solid ${colors.border}` : 'none', fontSize: '12px' }}>
                      <span style={{ color: colors.navy, fontWeight: 500 }}>{c.city}, {c.state}</span>
                      <span style={{ color: colors.muted }}>{c.offices} offices · <strong style={{ color: colors.gold }}>{Number(c.agents).toLocaleString()}</strong> agents</span>
                    </div>
                  ))}
                </Card>

                {/* Office size distribution */}
                <Card title="Office Size Distribution">
                  {(() => {
                    const d = insights.size_distribution || {};
                    const buckets = [
                      { label: '0 agents (empty)', count: d.empty_offices || 0, color: colors.muted },
                      { label: '1–5 agents', count: d.small_1_5 || 0, color: colors.blue },
                      { label: '6–20 agents', count: d.mid_6_20 || 0, color: colors.green },
                      { label: '21–50 agents', count: d.mid_21_50 || 0, color: colors.gold },
                      { label: '51–100 agents', count: d.large_51_100 || 0, color: colors.orange },
                      { label: '100+ agents', count: d.xlarge_100_plus || 0, color: colors.red },
                    ];
                    const max = Math.max(...buckets.map(b => b.count), 1);
                    const total = buckets.reduce((sum, b) => sum + Number(b.count), 0) || 1;
                    return buckets.map((b, i) => (
                      <div key={i} style={{ marginBottom: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '3px' }}>
                          <span style={{ color: colors.navy }}>{b.label}</span>
                          <span>
                            <span style={{ fontWeight: 700, color: b.color }}>{Number(b.count).toLocaleString()}</span>
                            <span style={{ color: colors.muted, marginLeft: '4px', fontSize: '10px' }}>({((Number(b.count) / total) * 100).toFixed(1)}%)</span>
                          </span>
                        </div>
                        <div style={{ height: '6px', background: colors.border, borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{ width: `${(b.count / max) * 100}%`, height: '100%', background: b.color, borderRadius: '3px' }} />
                        </div>
                      </div>
                    ));
                  })()}
                </Card>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                {/* Top brokerages */}
                <Card title="Top 10 Largest Brokerages">
                  {(insights.top_brokerages || []).map((b: any, i: number) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < 9 ? `1px solid ${colors.border}` : 'none', fontSize: '12px' }}>
                      <div>
                        <span style={{ fontWeight: 600, color: colors.navy }}>{i + 1}. {b.office_name}</span>
                        <span style={{ color: colors.muted, marginLeft: '6px', fontSize: '10px' }}>{b.city}, {b.state}</span>
                      </div>
                      <span style={{ fontWeight: 700, color: colors.gold, fontSize: '13px' }}>{Number(b.agent_count).toLocaleString()}</span>
                    </div>
                  ))}
                </Card>

                {/* Newest offices */}
                <Card title="Newest Offices (Recently Registered)">
                  {(insights.newest_offices || []).map((o: any, i: number) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < 9 ? `1px solid ${colors.border}` : 'none', fontSize: '12px' }}>
                      <div>
                        <span style={{ fontWeight: 600, color: colors.navy }}>{o.office_name}</span>
                        <span style={{ color: colors.muted, marginLeft: '6px', fontSize: '10px' }}>{o.city}, {o.state}</span>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ color: colors.green, fontWeight: 600, fontSize: '11px' }}>
                          {o.original_entry_date ? new Date(o.original_entry_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}
                        </span>
                        <span style={{ color: colors.muted, fontSize: '10px', marginLeft: '6px' }}>{Number(o.agent_count)} agents</span>
                      </div>
                    </div>
                  ))}
                </Card>
              </div>

              {/* Board breakdown */}
              <Card title="Board / Association Breakdown">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '8px' }}>
                  {(insights.board_breakdown || []).map((b: any, i: number) => {
                    const pct = stats.total_agents > 0 ? ((Number(b.agents) / stats.total_agents) * 100).toFixed(1) : '0';
                    return (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: i % 2 === 0 ? '#f9fafb' : 'transparent', borderRadius: '4px', fontSize: '12px' }}>
                        <span style={{ fontWeight: 600, color: colors.navy }}>{b.board}</span>
                        <span style={{ color: colors.muted }}>
                          {Number(b.offices).toLocaleString()} offices · <strong style={{ color: colors.blue }}>{Number(b.agents).toLocaleString()}</strong> agents · {pct}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>
          )}

              {/* Retention Analysis tab */}
              {detailTab === 'retention' && (
                <div>
                  {retentionLoading ? (
                    <p style={{ color: colors.muted, textAlign: 'center', padding: '40px 0' }}>Loading retention analysis...</p>
                  ) : !retentionData?.report ? (
                    <div style={{ background: colors.white, border: `1px solid ${colors.border}`, borderRadius: '10px', padding: '24px', textAlign: 'center' }}>
                      <p style={{ color: colors.muted, fontSize: '14px' }}>No retention report computed for this office yet.</p>
                      <p style={{ color: colors.muted, fontSize: '12px' }}>Run the retention computation script to generate this report.</p>
                    </div>
                  ) : (() => {
                    const r = retentionData.report;
                    const lostRev = (Number(r.lost_revenue_cents || 0) / 100);
                    const avgPrice = (Number(r.avg_sale_price_cents || 58180800) / 100);
                    const commPerDeal = avgPrice * 0.03;
                    const platformCost = Number(r.agent_count || 0) * 185 * 12;
                    const breakeven = Math.ceil(platformCost / commPerDeal);
                    const agents = r.agent_breakdown || [];
                    return (
                      <div>
                        {/* KPI Cards */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '16px' }}>
                          {[
                            { label: 'Lost Listings', value: r.lost_count?.toLocaleString() || '0', color: colors.red },
                            { label: 'Lost Revenue (3%)', value: `$${lostRev.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, color: colors.red },
                            { label: 'Retention Rate', value: `${Number(r.retention_rate || 0).toFixed(1)}%`, color: colors.green },
                            { label: 'Loss Rate', value: `${Number(r.loss_rate || 0).toFixed(1)}%`, color: colors.orange },
                          ].map((s, i) => (
                            <div key={i} style={{ background: colors.white, border: `1px solid ${colors.border}`, borderLeft: `3px solid ${s.color}`, borderRadius: '6px', padding: '12px' }}>
                              <div style={{ fontSize: '9px', color: colors.muted, textTransform: 'uppercase' }}>{s.label}</div>
                              <div style={{ fontSize: '20px', fontWeight: 700, color: colors.navy }}>{s.value}</div>
                            </div>
                          ))}
                        </div>

                        {/* ROI Calculator */}
                        <div style={{ background: colors.white, border: `1px solid ${colors.border}`, borderLeft: `4px solid ${colors.gold}`, borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                          <h6 style={{ fontSize: '11px', fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>KairosIQ ROI Calculator</h6>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                            <div>
                              <div style={{ fontSize: '10px', color: colors.muted }}>Platform Cost ({r.agent_count} agents × $185/mo)</div>
                              <div style={{ fontSize: '16px', fontWeight: 700, color: colors.navy }}>${platformCost.toLocaleString()}/yr</div>
                            </div>
                            <div>
                              <div style={{ fontSize: '10px', color: colors.muted }}>Break-even (clients to retain)</div>
                              <div style={{ fontSize: '16px', fontWeight: 700, color: colors.green }}>{breakeven} listings</div>
                            </div>
                            <div>
                              <div style={{ fontSize: '10px', color: colors.muted }}>ROI at 15% improvement</div>
                              <div style={{ fontSize: '16px', fontWeight: 700, color: colors.gold }}>
                                {((Math.round(Number(r.lost_count || 0) * 0.15) * commPerDeal - platformCost) / platformCost * 100).toFixed(0)}% return
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Agent Breakdown */}
                        {agents.length > 0 && (
                          <div style={{ background: colors.white, border: `1px solid ${colors.border}`, borderRadius: '10px', overflow: 'hidden' }}>
                            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${colors.border}` }}>
                              <h6 style={{ fontSize: '11px', fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>
                                Agent-Level Lost Deals ({agents.filter((a: any) => a.lost > 0).length} agents with losses)
                              </h6>
                            </div>
                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                              <table style={{ width: '100%', fontSize: '12px' }}>
                                <thead>
                                  <tr style={{ background: colors.offBg }}>
                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Agent</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>Buyer Deals</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>Retained</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>Lost</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>Lost Revenue</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>Retention %</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {agents.filter((a: any) => a.lost > 0 || a.buyer_deals > 0).slice(0, 50).map((a: any, i: number) => (
                                    <React.Fragment key={i}>
                                      <tr onClick={() => {
                                        if (selectedAgent?.mls_id === a.mls_id) { setSelectedAgent(null); setAgentLostDeals([]); return; }
                                        setSelectedAgent(a);
                                        setAgentDealsLoading(true);
                                        axiosInstance.get(`/broker/retention/agent/${a.mls_id}/lost-deals`).then(r => {
                                          if (r.data.success) setAgentLostDeals(r.data.deals || []);
                                          else setAgentLostDeals([]);
                                        }).catch(() => setAgentLostDeals([])).finally(() => setAgentDealsLoading(false));
                                      }} style={{ borderBottom: `1px solid ${colors.border}`, cursor: 'pointer', background: selectedAgent?.mls_id === a.mls_id ? '#f0f7ff' : 'transparent' }}>
                                        <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                                          {a.name || a.mls_id}
                                          <span style={{ fontSize: '10px', color: colors.muted, marginLeft: '6px' }}>{selectedAgent?.mls_id === a.mls_id ? '▼' : '▶'}</span>
                                        </td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>{a.buyer_deals}</td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right', color: colors.green }}>{a.retained}</td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right', color: colors.red, fontWeight: 700 }}>{a.lost}</td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right', color: colors.red, fontWeight: 700 }}>
                                          ${(Number(a.lost_revenue || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                        </td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                                          <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                                            background: a.retention_rate > 50 ? '#e8fde8' : a.retention_rate > 25 ? '#fff3e0' : '#ffebee',
                                            color: a.retention_rate > 50 ? colors.green : a.retention_rate > 25 ? colors.orange : colors.red }}>
                                            {a.retention_rate}%
                                          </span>
                                        </td>
                                      </tr>
                                      {selectedAgent?.mls_id === a.mls_id && (
                                        <tr>
                                          <td colSpan={6} style={{ padding: 0, background: '#f8fafd' }}>
                                            <div style={{ padding: '12px 16px' }}>
                                              {agentDealsLoading ? (
                                                <p style={{ color: colors.muted, fontSize: '12px', textAlign: 'center', padding: '10px 0' }}>Loading lost deals...</p>
                                              ) : agentLostDeals.length === 0 ? (
                                                <p style={{ color: colors.muted, fontSize: '12px', textAlign: 'center', padding: '10px 0' }}>No detailed deal data available yet. Run: node src/scripts/populate-agent-lost-deals.js --agent {a.mls_id}</p>
                                              ) : (
                                                <>
                                                  <div style={{ fontSize: '10px', fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                                                    {a.name}'s Lost Listings — {agentLostDeals.length} properties (${(agentLostDeals.reduce((s: number, d: any) => s + Number(d.lost_commission_cents || 0), 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })} lost)
                                                  </div>
                                                  <table style={{ width: '100%', fontSize: '11px' }}>
                                                    <thead>
                                                      <tr style={{ background: '#e8eef6' }}>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>Property</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>Purchase</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>Resale</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>Listed By</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>Lost Commission</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>Hold</th>
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {agentLostDeals.slice(0, 20).map((d: any, j: number) => (
                                                        <tr key={j} style={{ borderBottom: '1px solid #e9ecef' }}>
                                                          <td style={{ padding: '6px 8px' }}>
                                                            <div style={{ fontWeight: 500 }}>{d.property_address || '—'}</div>
                                                            <div style={{ color: colors.muted, fontSize: '10px' }}>{d.city} {d.zip}</div>
                                                          </td>
                                                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                                                            <div>${Number(d.purchase_price || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                                                            <div style={{ color: colors.muted, fontSize: '10px' }}>{d.purchase_date ? new Date(d.purchase_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}</div>
                                                          </td>
                                                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                                                            <div>${Number(d.resale_price || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                                                            <div style={{ color: colors.muted, fontSize: '10px' }}>{d.resale_date ? new Date(d.resale_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}</div>
                                                          </td>
                                                          <td style={{ padding: '6px 8px' }}>
                                                            <div style={{ fontWeight: 500, color: colors.red }}>{d.competitor_agent_name || d.competitor_agent_id || '—'}</div>
                                                            <div style={{ color: colors.muted, fontSize: '10px' }}>{d.competitor_office_name || ''}</div>
                                                          </td>
                                                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: colors.red }}>
                                                            ${(Number(d.lost_commission_cents || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                                          </td>
                                                          <td style={{ padding: '6px 8px', textAlign: 'right', color: colors.muted }}>
                                                            {d.hold_years ? Number(d.hold_years).toFixed(1) + 'yr' : '—'}
                                                          </td>
                                                        </tr>
                                                      ))}
                                                    </tbody>
                                                  </table>
                                                  {agentLostDeals.length > 20 && (
                                                    <p style={{ color: colors.muted, fontSize: '10px', textAlign: 'center', marginTop: '8px' }}>Showing top 20 of {agentLostDeals.length} lost deals</p>
                                                  )}
                                                </>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
        </div>
      </div>
    </div>
  );
};

const StatChip: React.FC<{ icon: any; label: string; value: string | number; color: string }> = ({ icon, label, value, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <FontAwesomeIcon icon={icon} style={{ color, fontSize: '12px' }} />
    <span style={{ color: colors.white, fontWeight: 700, fontSize: '16px' }}>{value}</span>
    <span style={{ color: colors.muted, fontSize: '11px' }}>{label}</span>
  </div>
);

const MiniStat: React.FC<{ label: string; value: string | number; color: string }> = ({ label, value, color }) => (
  <div style={{ background: colors.white, border: `1px solid ${colors.border}`, borderLeft: `3px solid ${color}`, borderRadius: '6px', padding: '10px 12px' }}>
    <div style={{ fontSize: '10px', color: colors.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
    <div style={{ fontSize: '16px', fontWeight: 700, color: colors.navy, marginTop: '2px' }}>{value}</div>
  </div>
);

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '10px', fontWeight: 700, color: colors.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{children}</th>
);

const Td: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <td style={{ padding: '10px 12px', color: colors.navy }}>{children}</td>
);

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ background: colors.white, border: `1px solid ${colors.border}`, borderLeft: `4px solid ${colors.gold}`, borderRadius: '10px' }}>
    <div style={{ padding: '12px 18px', borderBottom: `1px solid ${colors.border}` }}>
      <h5 style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: '1px' }}>{title}</h5>
    </div>
    <div style={{ padding: '14px 18px' }}>{children}</div>
  </div>
);

export default MarketAnalyticsPage;
